import { ItemType, MapFilter } from "../types";

// Shared color constants for the application
export const THEME_COLORS = {
  MEMBER: "#1b1a18", // Black for services/members
  EVENT: "#c82d28", // Red for events
  PRODUCT: "#7b9550", // Green for products
  PROJECT: "#3f9fcf", // Blue for projects
  SERVICE: "#e27e36", // Orange for professional services
  WHITE: "#ffffff", // White border
  BLUE: "#3b82f6", // Blue for connection arcs
} as const;

// Map marker color constants (keep for backward compatibility)
export const MAP_COLORS = {
  SERVICE_MARKER: THEME_COLORS.MEMBER,
  EVENT_MARKER: THEME_COLORS.EVENT,
  PRODUCT_MARKER: THEME_COLORS.PRODUCT,
  PROJECT_MARKER: THEME_COLORS.PROJECT,
  SERVICES_MARKER: THEME_COLORS.SERVICE,
  MARKER_BORDER: THEME_COLORS.WHITE,
  CONNECTION_ARC: THEME_COLORS.BLUE,
} as const;

// Utility function to lighten a hex color by a percentage
export function lightenColor(hexColor: string, percentage: number): string {
  // Remove # if present
  const hex = hexColor.replace("#", "");

  // Parse RGB values
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);

  // Calculate lightened values
  const lighten = (value: number) =>
    Math.round(value + (255 - value) * (percentage / 100));

  const newR = lighten(r);
  const newG = lighten(g);
  const newB = lighten(b);

  // Convert back to hex
  const toHex = (value: number) => value.toString(16).padStart(2, "0");

  return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
}

// Pre-calculated light versions for pills/badges
export const LIGHT_COLORS = {
  MEMBER: lightenColor(THEME_COLORS.MEMBER, 85),
  EVENT: lightenColor(THEME_COLORS.EVENT, 85),
  PRODUCT: lightenColor(THEME_COLORS.PRODUCT, 85),
  PROJECT: lightenColor(THEME_COLORS.PROJECT, 85),
  SERVICE: lightenColor(THEME_COLORS.SERVICE, 85),
} as const;

/**
 * Get the color for a given item type
 * @param itemType - The type of item (MEMBER, EVENT, PRODUCT, PROJECT, SERVICE)
 * @returns The hex color code for the item type
 */
export function getItemTypeColor(itemType: MapFilter): string {
  switch (itemType) {
    case "person":
      return THEME_COLORS.MEMBER;
    case "event":
      return THEME_COLORS.EVENT;
    case "product":
      return THEME_COLORS.PRODUCT;
    case "project":
      return THEME_COLORS.PROJECT;
    case "service":
      return THEME_COLORS.SERVICE;
    default:
      return "#6b7280"; // Gray fallback
  }
}

/**
 * Get a color with opacity for connection arcs
 * @param itemType - The type of item
 * @param opacity - The opacity level (0-1)
 * @returns RGBA color string
 */
export function getItemTypeColorWithOpacity(
  itemType: ItemType,
  opacity: number = 0.6
): string {
  const hexColor = getItemTypeColor(itemType);

  // Convert hex to RGB
  const r = parseInt(hexColor.slice(1, 3), 16);
  const g = parseInt(hexColor.slice(3, 5), 16);
  const b = parseInt(hexColor.slice(5, 7), 16);

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}
